import axios, { type AxiosResponse } from "axios";
import api from "./api";

// Create a raw axios instance without interceptors for the temporary subscription flow
const tempApi = axios.create({
  baseURL: process.env.REACT_APP_API_URL ,
});

export interface SubscriptionPlan {
  id: string;
  planName: string;
  price: number;
  currency: string;
  description?: string;
  durationType: string;
  durationValue: number;
  includedFeatures?: string[];
  planTag?: string;
  caseLimit?: number | null;
  userLimit?: number | null;
  platformTypes?: string[];
  platformType?: string;
  isVisible: boolean;
  isActive: boolean;
}

/**
 * Derive the platform types a plan applies to.
 * Handles the legacy single `platformType` field and defaults to hospital.
 */
export const getPlanPlatformTypes = (plan: SubscriptionPlan): string[] => {
  if (plan.platformTypes && plan.platformTypes.length > 0) return plan.platformTypes;
  if (plan.platformType) return [plan.platformType];
  return ["hospital"];
};

export interface CreatePlanPayload {
  planName: string;
  price: number;
  currency: string;
  description?: string;
  durationType: string;
  durationValue: number;
  includedFeatures?: string[];
  planTag?: string | null;
  caseLimit?: number | null;
  userLimit?: number | null;
  platformTypes?: string[];
  isVisible?: boolean;
  isActive?: boolean;
}

export interface UpdatePlanPayload extends Partial<CreatePlanPayload> { }

export interface SubscriptionResponse {
  success: boolean;
  message: string;
  tenantUrl?: string;
  welcomeEmailSent?: boolean;
  data?: {
    hospitalId: string;
    isSubscribed: boolean;
    subscriptionPlanId: string;
  };
}

/** Super Admin: get all plans */
export const getAllPlans = async (): Promise<SubscriptionPlan[]> => {
  const { data } = await api.get("/api/super-admin/subscription/all");
  return data.data ?? data;
};

/** Super Admin: create plan */
export const createPlan = async (payload: CreatePlanPayload) => {
  const { data } = await api.post("/api/super-admin/subscription/create", payload);
  return data;
};

/** Super Admin: update plan */
export const updatePlan = async (id: string, payload: UpdatePlanPayload) => {
  const { data } = await api.put(`/api/super-admin/subscription/update/${id}`, payload);
  return data;
};

/** Super Admin: delete plan */
export const deletePlan = async (id: string) => {
  const { data } = await api.delete(`/api/super-admin/subscription/delete/${id}`);
  return data;
};

/** Super Admin: toggle visible */
export const toggleVisible = async (id: string) => {
  const { data } = await api.patch(`/api/super-admin/subscription/toggle-visible/${id}`);
  return data;
};

/** Hospital: get public plans (no auth) */
export const getPublicPlans = async (): Promise<SubscriptionPlan[]> => {
  const { data } = await api.get("/api/hospital/subscription/public-plans");
  return data.data ?? data;
};

/** Hospital: subscribe (auth required) - sends only subscriptionPlanId in body */
export const subscribe = async (planId: string, token?: string): Promise<AxiosResponse<SubscriptionResponse>> => {
  if (process.env.NODE_ENV !== "production") {
    console.debug("[Subscription] Using temp token:", token ? `${token.substring(0, 10)}...` : "none");
  }
  const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
  return tempApi.post<SubscriptionResponse>("/api/hospital/subscription/subscribe", {
    subscriptionPlanId: planId,
  }, config);
};

// ── Unified Subscription Listing API ────────────────────────────

export type SubscriberType = "hospital" | "doctor" | "public";
export type SubscriptionStatus = "Active" | "Pending" | "Expired" | "Cancelled";

export interface SubscriptionItem {
  id: string;
  subscriberName: string;
  subscriberType: SubscriberType;
  subscriberId?: string;
  email: string;
  subscriptionPlanId?: string;
  planName: string;
  billingCycle: "Monthly" | "Annual";
  amount: number | string;
  currency: string;
  status: SubscriptionStatus;
  startDate: string;
  endDate: string;
  autoRenew?: boolean;
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
  plan?: SubscriptionPlan;
}

export interface SubscriptionListParams {
  search?: string;
  status?: string;
  subscriberType?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: "ASC" | "DESC";
}

export interface SubscriptionListResponse {
  subscriptions: SubscriptionItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/** Super Admin: list unified subscriptions with search, filter, pagination */
export const listSubscriptions = async (
  params?: SubscriptionListParams
): Promise<SubscriptionListResponse> => {
  const { data } = await api.get("/api/super-admin/subscriptions", { params });
  return data.data ?? data;
};

/** Super Admin: get subscription details by ID */
export const getSubscriptionById = async (
  id: string
): Promise<SubscriptionItem> => {
  const { data } = await api.get(`/api/super-admin/subscriptions/${id}`);
  return data.data ?? data;
};

/** Super Admin: update subscription */
export const updateSubscription = async (
  id: string,
  payload: Partial<SubscriptionItem>
): Promise<SubscriptionItem> => {
  const { data } = await api.put(`/api/super-admin/subscriptions/${id}`, payload);
  return data.data ?? data;
};

// ── Subscription Dashboard API & Types ─────────────────────────

export interface KpiBreakdown {
  active: number;
  pending: number;
  expired: number;
  cancelled?: number;
}

export interface RevenueBreakdown {
  hospitalRevenue: number;
  hospitalRevenueFormatted: string;
  doctorRevenue: number;
  doctorRevenueFormatted: string;
  publicRevenue: number;
  publicRevenueFormatted: string;
}

export interface KpiWidgetData {
  count: number;
  changeText: string;
  changeType: "positive" | "negative" | "neutral";
  breakdown: KpiBreakdown;
}

export interface RevenueKpiWidgetData {
  amount: number;
  formatted: string;
  changeText: string;
  changeType: "positive" | "negative" | "neutral";
  breakdown: RevenueBreakdown;
}

export interface RevenueTrendItem {
  month: string;
  fullMonth: string;
  hospitalRevenue: number;
  doctorRevenue: number;
  publicRevenue: number;
  totalRevenue: number;
  byPlan?: Record<string, number>;
}

export interface PlanDistributionItem {
  planId: string;
  planName: string;
  hospitals: number;
  doctors: number;
  publicUsers: number;
  total: number;
}

export interface SubscriptionStatusUsage {
  label: string;
  count: number;
  color: string;
  percentage: number;
  byType: {
    hospital: number;
    doctor: number;
    public: number;
  };
}

export interface SubscriptionUsageData {
  total: number;
  statuses: SubscriptionStatusUsage[];
}

export interface TopPlanItem {
  planId: string;
  planName: string;
  subscriptions: number;
  totalSubscriptions: number;
  revenue: number;
  revenueFormatted: string;
}

export interface SubscriptionDashboardStats {
  dateRange: {
    startDate: string;
    endDate: string;
    label: string;
    preset: string;
  };
  kpis: {
    totalHospitals: KpiWidgetData;
    totalDoctors: KpiWidgetData;
    totalPublicUsers: KpiWidgetData;
    totalSubscriptions: KpiWidgetData;
    totalRevenue: RevenueKpiWidgetData;
  };
  revenueTrend: RevenueTrendItem[];
  planDistribution: PlanDistributionItem[];
  subscriptionUsage: SubscriptionUsageData;
  topPlans?: TopPlanItem[];
}

export interface DashboardQueryParams {
  startDate?: string;
  endDate?: string;
  preset?: string;
}

/** Super Admin: get Subscription Dashboard stats */
export const getSubscriptionDashboardStats = async (
  params?: DashboardQueryParams
): Promise<SubscriptionDashboardStats> => {
  const { data } = await api.get("/api/super-admin/subscriptions/dashboard", { params });
  return data.data ?? data;
};


